import type {
  Experiment,
  ExperimentCategory,
  GoalCode,
  PathwayStyle,
} from "@andrum/schemas";
import { t } from "@/i18n";

/** Anonymous global performance for one experiment */
export type ExperimentGlobalStat = {
  experimentId: string;
  nOutcomes: number;
  globalWeight: number; // 0..1 composite success
  completionRate?: number | null;
  avgMoodDelta?: number | null;
  repeatYesRate?: number | null;
};

/** Experiments that worked for similar people (no PII) */
export type SimilarSignal = {
  experimentId: string;
  score: number; // 0..1
  support: number;
};

export type RecommendContext = {
  /** User goals from onboarding (goal_code) */
  goals?: string[];
  /** How user prefers joy: social | solo | both */
  joyPathway?: PathwayStyle | string | null;
  /** How user prefers calm: social | solo | both */
  calmPathway?: PathwayStyle | string | null;
  /** Multi life situation codes from onboarding / profile */
  lifeSituations?: string[];
  /** Latest check-in mood 0-10 */
  mood?: number | null;
  energy?: number | null;
  stress?: number | null;
  /** Max minutes user said they have */
  availableMinutes?: number | null;
  /** Experiment IDs completed recently - avoid repeating immediately */
  recentExperimentIds?: string[];
  /** Explicitly exclude these experiment IDs (e.g. current assignment) */
  excludeIds?: string[];
  /**
   * Experiment IDs the user marked as positive (high repeat intent / after mood).
   * Prefer similar or same experiments later.
   */
  positiveExperimentIds?: string[];
  /**
   * Categories that scored well historically for this user.
   */
  positiveCategories?: string[];
  /**
   * Experiment IDs the user marked as weak (low repeat intent / low after mood).
   * Softly deprioritize without hard-banning.
   */
  negativeExperimentIds?: string[];
  /** If false, skip premium catalog items (default true = allow all) */
  allowPremium?: boolean;
  /**
   * Global anonymous weights per experiment (from population aggregates).
   */
  globalStats?: ExperimentGlobalStat[];
  /**
   * Signals from people with similar goals / check-ins.
   */
  similarSignals?: SimilarSignal[];
  /**
   * Explore probability for epsilon-greedy bandit (default 0.12).
   * 0 = pure exploit, 1 = pure random among candidates.
   */
  exploreEpsilon?: number;
  /**
   * Deterministic seed for bandit RNG (tests). If omitted, uses time+id salt.
   */
  rngSeed?: number;
};

export type Recommendation = {
  experiment: Experiment;
  reasonCode: string;
  reasonLabel: string;
  score: number;
};

const GOAL_TO_CATEGORIES: Record<string, ExperimentCategory[]> = {
  energy: ["movement", "recovery", "presence"],
  joy: ["gratitude", "connection", "presence"],
  calm: ["recovery", "presence", "gratitude"],
  connection: ["connection"],
  meaning: ["meaning", "gratitude"],
  alone_time: ["presence", "recovery", "meaning"],
};

/** Categories preferred for social vs solo pathways */
const SOCIAL_CATS: ExperimentCategory[] = ["connection", "gratitude"];
const SOLO_CATS: ExperimentCategory[] = ["presence", "recovery", "meaning"];

function categoriesForGoal(
  goal: string,
  joyPathway?: string | null,
  calmPathway?: string | null
): ExperimentCategory[] {
  const base = GOAL_TO_CATEGORIES[goal] ?? [];
  if (goal === "joy") {
    const p = joyPathway ?? "both";
    if (p === "social") return ["connection", "gratitude", "presence"];
    if (p === "solo") return ["presence", "gratitude", "meaning"];
    return base;
  }
  if (goal === "calm") {
    const p = calmPathway ?? "both";
    if (p === "social") return ["connection", "gratitude", "recovery"];
    if (p === "solo") return ["recovery", "presence"];
    return base;
  }
  if (goal === "alone_time") {
    return SOLO_CATS;
  }
  return base;
}

/** Reason codes, labels resolved via i18n at call time. */
export const REASON_CODES = [
  "default",
  "connection",
  "recovery",
  "gratitude",
  "presence",
  "movement",
  "meaning",
  "stress",
  "low_energy",
  "goal",
  "short_time",
  "rotation",
  "pick",
  "program",
  "worked_before",
  "category_win",
  "global_win",
  "similar_people",
  "explore",
] as const;

export function getReasonLabel(code: string): string {
  const key = `experiment.reasons.${code}`;
  const value = t(key);
  if (value === key || value.startsWith("experiment.reasons.")) {
    return t("experiment.reasons.default");
  }
  return value;
}

/** @deprecated use getReasonLabel, kept for tests that expect a map shape */
export const REASON_LABELS: Record<string, string> = new Proxy(
  {} as Record<string, string>,
  {
    get(_target, prop: string) {
      return getReasonLabel(prop);
    },
  }
);

function maxMinutesFromContext(ctx: RecommendContext): number | null {
  if (ctx.availableMinutes != null && ctx.availableMinutes > 0) {
    return ctx.availableMinutes;
  }
  return null;
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function scoreExperiment(
  exp: Experiment,
  ctx: RecommendContext,
  totalOutcomes: number
): { score: number; reasonCode: string } {
  let score = 10;
  let reasonCode: string = exp.category;

  const preferred = new Set<ExperimentCategory>();
  for (const goal of ctx.goals ?? []) {
    for (const cat of categoriesForGoal(
      goal,
      ctx.joyPathway,
      ctx.calmPathway
    )) {
      preferred.add(cat);
    }
  }

  if (preferred.has(exp.category)) {
    score += 25;
    reasonCode = "goal";
  }

  // Pathway soft boost/penalty for joy/calm style
  const joyP = ctx.joyPathway ?? null;
  const calmP = ctx.calmPathway ?? null;
  if (
    (ctx.goals ?? []).includes("joy") &&
    joyP === "social" &&
    SOCIAL_CATS.includes(exp.category)
  ) {
    score += 12;
  }
  if (
    (ctx.goals ?? []).includes("joy") &&
    joyP === "solo" &&
    SOLO_CATS.includes(exp.category)
  ) {
    score += 12;
  }
  if (
    (ctx.goals ?? []).includes("joy") &&
    joyP === "solo" &&
    exp.category === "connection"
  ) {
    score -= 14;
  }
  if (
    (ctx.goals ?? []).includes("joy") &&
    joyP === "social" &&
    exp.category === "meaning" &&
    !preferred.has("meaning")
  ) {
    score -= 4;
  }
  if (
    (ctx.goals ?? []).includes("calm") &&
    calmP === "solo" &&
    (exp.category === "recovery" || exp.category === "presence")
  ) {
    score += 10;
  }
  if (
    (ctx.goals ?? []).includes("calm") &&
    calmP === "social" &&
    exp.category === "connection"
  ) {
    score += 8;
  }

  // Life situation: soft realism (shorter when parenting/working heavily)
  const life = new Set(ctx.lifeSituations ?? []);
  if (
    life.has("parent_of_small_children") ||
    life.has("parental_leave")
  ) {
    if (exp.durationMinutes <= 5) score += 8;
    if (exp.durationMinutes >= 10) score -= 6;
  }
  if (life.has("working") && life.has("studying")) {
    if (exp.durationMinutes <= 5) score += 6;
  }

  // Personal history
  if (ctx.positiveExperimentIds?.includes(exp.id)) {
    score += 35;
    reasonCode = "worked_before";
  } else if (ctx.positiveCategories?.includes(exp.category)) {
    score += 18;
    if (reasonCode === exp.category || reasonCode === "goal") {
      reasonCode = "category_win";
    }
  }
  if (ctx.negativeExperimentIds?.includes(exp.id)) {
    score -= 22;
  }

  // Global anonymous weights
  const gStat = ctx.globalStats?.find((s) => s.experimentId === exp.id);
  if (gStat && gStat.nOutcomes >= 3) {
    // Map weight 0..1 → roughly -8..+18
    const boost = (gStat.globalWeight - 0.5) * 36;
    score += boost;
    if (gStat.globalWeight >= 0.62 && reasonCode !== "worked_before") {
      reasonCode = "global_win";
    }
  }

  // UCB-style exploration bonus for under-sampled experiments
  const n_i = gStat?.nOutcomes ?? 0;
  const N = Math.max(totalOutcomes, 1);
  const exploreBonus = 12 * Math.sqrt(Math.log(N + 2) / (n_i + 1));
  score += exploreBonus;

  // Similar people
  const sim = ctx.similarSignals?.find((s) => s.experimentId === exp.id);
  if (sim && sim.support >= 1) {
    score += sim.score * 22;
    if (sim.score >= 0.65 && sim.support >= 2 && reasonCode !== "worked_before") {
      reasonCode = "similar_people";
    }
  }

  if (ctx.stress != null && ctx.stress >= 7) {
    if (exp.category === "recovery" || exp.category === "presence") {
      score += 30;
      reasonCode = "stress";
    } else if (exp.difficulty >= 3) {
      score -= 15;
    }
  }

  if (ctx.energy != null && ctx.energy <= 3) {
    if (exp.durationMinutes <= 3 || exp.difficulty <= 2) {
      score += 20;
      reasonCode = "low_energy";
    }
    if (exp.category === "movement" && exp.durationMinutes <= 5) {
      score += 8;
    }
  }

  if (ctx.mood != null && ctx.mood <= 3) {
    if (exp.category === "connection" || exp.category === "gratitude") {
      score += 12;
    }
  }

  const cap = maxMinutesFromContext(ctx);
  if (cap != null) {
    if (exp.durationMinutes <= cap) {
      score += 10;
      if (exp.durationMinutes <= Math.min(cap, 3)) {
        reasonCode = reasonCode === exp.category ? "short_time" : reasonCode;
      }
    } else if (exp.minDurationMinutes <= cap) {
      score += 2;
    } else {
      score -= 20;
    }
  }

  if (ctx.recentExperimentIds?.includes(exp.id)) {
    score -= 40;
  }

  // Light jitter so rotation isn't deterministic only by id order
  const day = Math.floor(Date.now() / 86_400_000);
  const salt = (exp.id.charCodeAt(exp.id.length - 1) + day) % 7;
  score += salt;

  return { score, reasonCode };
}

/**
 * Rank catalog and return best recommendation (or null if empty).
 * Uses epsilon-greedy bandit: with probability epsilon explore among
 * under-sampled / mid-ranked candidates; otherwise exploit top score.
 */
export function recommendExperiment(
  catalog: Experiment[],
  ctx: RecommendContext = {}
): Recommendation | null {
  const exclude = new Set(ctx.excludeIds ?? []);
  const allowPremium = ctx.allowPremium !== false;
  const candidates = catalog.filter((e) => {
    if (exclude.has(e.id)) return false;
    if (!allowPremium && e.premium) return false;
    return true;
  });
  if (candidates.length === 0) return null;

  const totalOutcomes =
    ctx.globalStats?.reduce((a, s) => a + s.nOutcomes, 0) ?? candidates.length;

  const ranked = candidates.map((experiment) => {
    const { score, reasonCode } = scoreExperiment(
      experiment,
      ctx,
      totalOutcomes
    );
    return {
      experiment,
      reasonCode,
      reasonLabel: getReasonLabel(reasonCode),
      score,
    };
  });

  ranked.sort((a, b) => b.score - a.score);

  const epsilon =
    ctx.exploreEpsilon != null
      ? Math.min(1, Math.max(0, ctx.exploreEpsilon))
      : 0.12;

  const seed =
    ctx.rngSeed ??
    (Math.floor(Date.now() / 60_000) ^
      (candidates[0]!.id.charCodeAt(0) * 997));
  const rng = mulberry32(seed);
  const roll = rng();

  // Explore: pick among under-sampled or mid-tier (not pure random garbage)
  if (roll < epsilon && ranked.length > 1) {
    const underSampled = ranked.filter((r) => {
      const n =
        ctx.globalStats?.find((s) => s.experimentId === r.experiment.id)
          ?.nOutcomes ?? 0;
      return n < 5;
    });
    const pool =
      underSampled.length > 0
        ? underSampled
        : ranked.slice(0, Math.min(8, ranked.length));
    const pick = pool[Math.floor(rng() * pool.length)]!;
    return {
      ...pick,
      reasonCode: "explore",
      reasonLabel: getReasonLabel("explore"),
    };
  }

  return ranked[0] ?? null;
}

/**
 * Map goal codes / free strings to categories for filters.
 */
export function categoriesForGoals(
  goals: GoalCode[] | string[]
): ExperimentCategory[] {
  const set = new Set<ExperimentCategory>();
  for (const goal of goals) {
    for (const cat of GOAL_TO_CATEGORIES[goal] ?? []) {
      set.add(cat);
    }
  }
  return Array.from(set);
}
